import type { AxiosInstance, AxiosResponse } from 'axios';
import type { LocalizationLanguage } from '@gov-nx/core/app';
import type {
	RezaOpravneniDto,
	RezaOpravneniSeznamDto,
	RezaReklamaceOpravneniPozadavekDto,
	RezaSablonaDto,
	RezaSablonaSeznamDto,
	RezaPdfPrehledStahnoutPozadavekDto,
	RezaPdfVyuzitiStahnoutPozadavekDto,
	RezaPdfPrehledOdeslatPozadavekDto,
	RezaPdfVyuzitiOdeslatPozadavekDto,
	RezaPdfOpravneniOdeslatPozadavekDto,
} from '../generated';

export interface EligibleListParams {
	text?: string;
	role?: 'zmocnenec' | 'zmocnitel';
	typ?: 'implicitni' | 'explicitni';
	kod?: string;
	stav?: 'platne' | 'archivni';
	platnostOdOd?: string;
	platnostOdDo?: string;
	platnostDoOd?: string;
	platnostDoDo?: string;
	kodSablony?: string[];
}

export const rezaEligibleList = async (
	axiosInstance: AxiosInstance,
	params: EligibleListParams
): Promise<RezaOpravneniSeznamDto> => {
	const response = await axiosInstance.get<RezaOpravneniSeznamDto>(
		'/api/reza/v1/opravneni',
		{
			params,
			paramsSerializer: {
				indexes: false,
			},
		}
	);
	return response.data;
};

export const rezaEligibleDetail = async (
	axiosInstance: AxiosInstance,
	code: string
): Promise<RezaOpravneniDto> => {
	const response = await axiosInstance.get<RezaOpravneniDto>(
		`/api/reza/v1/opravneni/${code}`
	);
	return response.data;
};

export const rezaDownloadQuery = async ({
	axiosInstance,
	code,
}: {
	axiosInstance: AxiosInstance;
	code: string;
}): Promise<AxiosResponse<Blob>> =>
	await axiosInstance.get(`/api/reza/v1/opravneni/${code}/stahnout`, {
		responseType: 'blob',
	});

export const rezaSendByDataboxQuery = async ({
	axiosInstance,
	code,
	body,
}: {
	axiosInstance: AxiosInstance;
	code: string;
	body: RezaPdfOpravneniOdeslatPozadavekDto;
}): Promise<AxiosResponse<void>> => {
	const response = await axiosInstance.post(
		`/api/reza/v1/opravneni/${code}/odeslat`,
		body
	);
	return response.data;
};
interface TemplateParams {
	text?: string;
	kod?: string;
	platnostOd?: string;
	platnostDo?: string;
}

export const rezaTemplates = async (
	axiosInstance: AxiosInstance,
	params: TemplateParams
): Promise<RezaSablonaSeznamDto> => {
	const response = await axiosInstance.get<RezaSablonaSeznamDto>(
		'/api/reza/v1/sablony',
		{ params }
	);
	return response.data;
};

export const rezaTemplate = async (
	axiosInstance: AxiosInstance,
	code: string
): Promise<RezaSablonaDto> => {
	const response = await axiosInstance.get<RezaSablonaDto>(
		`/api/reza/v1/sablony/${code}`
	);
	return response.data;
};

export const rezaSubmitComplaint = async ({
	axiosInstance,
	payload,
}: {
	axiosInstance: AxiosInstance;
	payload: RezaReklamaceOpravneniPozadavekDto;
}): Promise<void> => {
	const response = await axiosInstance.post(
		'/api/reza/v1/opravneni/reklamace',
		payload
	);
	return response.data;
};

export interface RezaPortalInfo {
	id: number;
	template: string; //'SOZ-1-2';
	portalName: string; //'Portál dopravy';
	portalURL: string; // 'https://portaldopravy.cz';
	portalDescription: string; // '';
}

export const rezaTemplatePortalInfoQuery = async (
	axiosCmsInstance: AxiosInstance,
	templateId: string,
	language: LocalizationLanguage
): Promise<RezaPortalInfo> => {
	const response = await axiosCmsInstance.get(
		`reza-portals/${templateId}-${language}.json`
	);

	return response.data.data;
};

export const rezaOverviewDownloadQuery = async ({
	axiosInstance,
	headers,
	body,
}: {
	axiosInstance: AxiosInstance;
	headers?: { 'x-okz': string };
	body: RezaPdfPrehledStahnoutPozadavekDto;
}): Promise<AxiosResponse<Blob>> => {
	const response = await axiosInstance.post(
		'/api/reza/v1/pdf/prehled/stahnout',
		body,
		{
			responseType: 'blob',
			headers,
		}
	);
	return response;
};

export const rezaOverviewSendByDataboxQuery = async ({
	axiosInstance,
	headers,
	body,
}: {
	axiosInstance: AxiosInstance;
	headers?: { 'x-okz': string };
	body: RezaPdfPrehledOdeslatPozadavekDto;
}): Promise<void> => {
	const response = await axiosInstance.post(
		'/api/reza/v1/pdf/prehled/odeslat',
		body,
		{
			headers,
		}
	);
	return response.data;
};

export const rezaUsageDownloadQuery = async ({
	axiosInstance,
	body,
}: {
	axiosInstance: AxiosInstance;
	body: RezaPdfVyuzitiStahnoutPozadavekDto;
}): Promise<AxiosResponse<Blob>> => {
	const response = await axiosInstance.post(
		'/api/reza/v1/pdf/vyuziti/stahnout',
		body,
		{
			responseType: 'blob',
		}
	);
	return response;
};

export const rezaUsageSendByDataboxQuery = async ({
	axiosInstance,
	body,
}: {
	axiosInstance: AxiosInstance;
	body: RezaPdfVyuzitiOdeslatPozadavekDto;
}): Promise<void> => {
	const response = await axiosInstance.post(
		'/api/reza/v1/pdf/vyuziti/odeslat',
		body
	);
	return response.data;
};
